import { put, take, fork, call, select, all } from 'redux-saga/effects';

import { FileMaceClaimAction, FileMaceClaimScope } from './types';
import { ScopeErrorAction } from '../scope-error/types';

import * as Api from '../../service/api';

import { getActiveMaceClaim, getMaceClaims, isCustomerInfoModify, mapApiInsertedMaceClaim } from './helpers';
import { formatPhoneNumber, getPlans } from '../user/helpers';

import {
  InsertMaceClaimDto,
  InsertComplaintAndTroubleshootDto,
  UpdateCustomerInfoDto,
  UpdateProductInfo,
  SubmitMaceClaimDto,
} from '@/core-types/file-mace-claim';
import { UserAction } from '../user/types';

import { fetchPlansAndClaims } from '../user/actions';
import { removeActiveMaceClaim } from './actions';
import { enableScopeLoading, disableScopeLoading } from '../scope-loading/actions';
import { MaceClaim } from '@/core-types/claim';
import { ClaimStatus } from '@/core-types/claim-status';
import { MacePlan } from '@/core-types/plan';

function* insertMaceClaimFlow() {
  while (true) {
    const request = yield take(FileMaceClaimAction.INSERT_CLAIM);
    yield put(enableScopeLoading(FileMaceClaimScope));
    try {
      const dto: InsertMaceClaimDto = request.payload;
      const maceClaims = yield call(Api.fileMaceClaim.insertClaim, dto);
      if (!maceClaims || !maceClaims?.claim_id) {
        throw new Error('Something went wrong. PLease try again later.');
      }
      const workingMaceClaim = mapApiInsertedMaceClaim(maceClaims);
      const activeMaceClaim = yield select(getActiveMaceClaim);

      const plans: MacePlan[] = yield select(getPlans);

      const plan = plans.find((plan) => plan?.id === dto.warrentyId) || {};

      const activeMaceClaimUpdated: MaceClaim = {
        ...(activeMaceClaim ?? {}),
        ...workingMaceClaim,
        plan,
        claimDate: new Date(),
        status: ClaimStatus.DRAFT,
      };

      yield put({
        type: FileMaceClaimAction.UPDATE_ACTIVE_CLAIM,
        payload: activeMaceClaimUpdated,
      });

      const userMaceClaims: MaceClaim[] = yield select(getMaceClaims);
      yield put({
        type: UserAction.SET_CLAIMS,
        payload: [...userMaceClaims, activeMaceClaimUpdated],
      });
    } catch (error) {
      console.log(error);

      yield put({ type: ScopeErrorAction.SET_SCOPE_ERROR, payload: FileMaceClaimScope, error });
    } finally {
      yield put(disableScopeLoading(FileMaceClaimScope));
    }
  }
}

function* updateCustonerInfoFlow() {
  while (true) {
    const request = yield take(FileMaceClaimAction.UPDATE_CUSTOMER_INFO);
    yield put(enableScopeLoading(FileMaceClaimScope));
    try {
      const activeMaceClaim: MaceClaim = yield select(getActiveMaceClaim);
      const dto: UpdateCustomerInfoDto = request.payload;
      const plans: MacePlan[] = yield select(getPlans);

      if (isCustomerInfoModify(activeMaceClaim.plan, dto)) {
        yield call(Api.fileMaceClaim.updateCustomerInfo, dto);

        for (let index = 0; index < plans?.length; index++) {
          const element = plans[index];
          if (element.customerNumber === dto.customerNumber) {
            const updatePlan = {
              ...element,
              customerFirstName: dto?.firstName || '',
              customerLastName: dto?.lastName || '',
              customerAddressLine1: dto?.address?.[0]?.addressLine1 || '',
              customerAddressLine2: dto?.address?.[0]?.addressLine2 || '',
              customerCity: dto?.address?.[0]?.city || '',
              customerState: dto?.address?.[0]?.state || '',
              customerCountry: dto?.address?.[0]?.country || '',
              customerZip: dto?.address?.[0]?.zip || '',
              customerPhoneNumber: formatPhoneNumber(dto?.address?.[0]?.customerPhone?.[0]?.number || '') || '',
              customerPhoneType: dto.address?.[0]?.customerPhone?.[0]?.phoneType || '',
              customerPhoneExt: dto.address?.[0]?.customerPhone?.[0]?.phoneExt || '',
              customerEmail: dto.address?.[0]?.email || '',
              customerAddressCode: dto.address?.[0]?.addressCode || '',
            };
            plans[index] = updatePlan;

            const activeMaceClaimUpdated = {
              ...(activeMaceClaim ?? {}),
              claimDate: new Date(),
              status: ClaimStatus.DRAFT,
              plan: updatePlan,
            };

            yield put({
              type: FileMaceClaimAction.UPDATE_ACTIVE_CLAIM,
              payload: activeMaceClaimUpdated,
            });
            break;
          }
        }

        yield put({
          type: UserAction.SET_PLANS,
          payload: plans,
        });
      }
    } catch (error) {
      yield put({ type: ScopeErrorAction.SET_SCOPE_ERROR, payload: FileMaceClaimScope, error });
    } finally {
      yield put(disableScopeLoading(FileMaceClaimScope));
    }
  }
}

function* insertComplaintAndTroubleshoot() {
  while (true) {
    const request = yield take(FileMaceClaimAction.INSERT_COMPLAIT_AND_TROUBLESHOOT);
    yield put(enableScopeLoading(FileMaceClaimScope));
    try {
      const dto: InsertComplaintAndTroubleshootDto = request.payload;
      const maceClaims = yield call(Api.fileMaceClaim.insertComplaintTroubleshoot, dto);
      const activeMaceClaim = yield select(getActiveMaceClaim);

      const activeMaceClaimUpdated = {
        ...(activeMaceClaim ?? {}),
        ...dto,
      };

      yield put({
        type: FileMaceClaimAction.UPDATE_ACTIVE_CLAIM,
        payload: activeMaceClaimUpdated,
      });
    } catch (error) {
      yield put({ type: ScopeErrorAction.SET_SCOPE_ERROR, payload: FileMaceClaimScope, error });
    } finally {
      yield put(disableScopeLoading(FileMaceClaimScope));
    }
  }
}

function* submitMaceClaimFlow() {
  while (true) {
    const request = yield take(FileMaceClaimAction.SUBMIT_CLAIM);

    yield put(enableScopeLoading(FileMaceClaimScope));
    try {
      const { warrentyId, claimId }: SubmitMaceClaimDto = request.payload;
      if (yield call(Api.fileMaceClaim.submitClaim, claimId, { warrentyId })) {
        yield put(fetchPlansAndClaims());
        yield call(removeActiveMaceClaim);
        localStorage.removeItem(`selectedItemsInfo${claimId}`);
        localStorage.removeItem(`troubleShoot${claimId}`);
        localStorage.removeItem(`selectedOptions${claimId}`);

        yield put({
          type: FileMaceClaimAction.CLEAR_ERROR_STEPS,
          payload: claimId,
        });
      }
    } catch (error) {
      yield put({ type: ScopeErrorAction.SET_SCOPE_ERROR, payload: FileMaceClaimScope, error });
    } finally {
      yield put(disableScopeLoading(FileMaceClaimScope));
    }
  }
}

function* insertMaceClaimItemFlow() {
  while (true) {
    const request = yield take(FileMaceClaimAction.INSERT_WARRANTY_ITEM);

    yield put(enableScopeLoading(FileMaceClaimScope));
    try {
      const dto: UpdateProductInfo = request.payload;
      yield call(Api.fileMaceClaim.insertClaimItems, {
        contractNumber: dto?.contractNumber,
        products: dto?.products,
      });

      yield all(
        dto.products?.map(function* (products) {
          if (
            !(yield call(Api.fileMaceClaim.insertClaimItemUpdateWarrentyItem, {
              selectedItemId: products?.Id,
              warrentyId: dto.planId,
              claimId: dto.cliamId,
            }))
          ) {
            throw new Error('warrnty items not added in calim');
          }
        }),
      );
      const activeMaceClaim = yield select(getActiveMaceClaim);

      const oldWranntyItems = activeMaceClaim?.plan?.warrantyItems || [];
      const activeMaceClaimUpdated = {
        ...(activeMaceClaim ?? {}),
        items: dto.products?.map((item) => ({
          ...(oldWranntyItems.find((i) => i?.id === item?.Id) || {}),
          ...item,
        })),
      };

      yield put({
        type: FileMaceClaimAction.UPDATE_ACTIVE_CLAIM,
        payload: activeMaceClaimUpdated,
      });
    } catch (error) {
      yield put({ type: ScopeErrorAction.SET_SCOPE_ERROR, payload: FileMaceClaimScope, error });
    } finally {
      yield put(disableScopeLoading(FileMaceClaimScope));
    }
  }
}

export default function* fileMaceClaimSaga() {
  yield fork(insertMaceClaimFlow);
  yield fork(updateCustonerInfoFlow);
  yield fork(insertMaceClaimItemFlow);
  yield fork(insertComplaintAndTroubleshoot);
  yield fork(submitMaceClaimFlow);
}
